/**
 * API service layer for PSM Shipping Portal.
 * Provides centralized API calls using axios with credentials.
 */
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Authentication API endpoints.
 */
export const authAPI = {
  login: (userId, password, recaptchaToken) => api.post('/auth/login', { userId, password, recaptchaToken }),
  logout: () => api.post('/auth/logout'),
  checkSession: () => api.get('/auth/session'),
  changePassword: (newPassword) => api.post('/auth/change-password', { newPassword }),
};

/**
 * Shipments API endpoints.
 */
export const shipmentsAPI = {
  getAll: () => api.get('/shipments'),
  getById: (id) => api.get(`/shipments/${id}`),
  create: (data) => api.post('/shipments', data),
  update: (id, data) => api.put(`/shipments/${id}`, data),
  reject: (id, reason) => api.post(`/shipments/${id}/reject`, { rejection_reason: reason }),
  approve: (id) => api.post(`/shipments/${id}/approve`),
  clone: (id) => api.post(`/shipments/${id}/clone`),
  delete: (id) => api.post(`/shipments/${id}/delete`),  // Changed from DELETE to POST for IIS compatibility
  sendNotification: (id) => api.post(`/shipments/${id}/send-notification`),
};

/**
 * File upload API endpoints.
 */
export const uploadAPI = {
  uploadFile: (file, shipmentId) => {
    const formData = new FormData();
    formData.append('file', file);
    if (shipmentId) {
      formData.append('shipment_id', shipmentId);
    }
    return api.post('/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

/**
 * Attachments API endpoints.
 */
export const attachmentsAPI = {
  getForShipment: (shipmentId) => api.get(`/shipments/${shipmentId}/attachments`),
  getDownloadUrl: (attachmentId) => `${API_BASE_URL}/attachments/${attachmentId}/download`,
  delete: (attachmentId) => api.post(`/attachments/${attachmentId}/delete`),  // POST for IIS compatibility
};

/**
 * Address book API endpoints.
 */
export const addressAPI = {
  getAll: (type) => api.get('/addresses', { params: { type } }),
  search: (query) => api.get('/addresses/search', { params: { q: query } }),
  getById: (id) => api.get(`/addresses/${id}`),
  create: (data) => api.post('/addresses', data),
  update: (id, data) => api.post(`/addresses/${id}/update`, data),  // Changed from PUT to POST for IIS compatibility
  delete: (id) => api.post(`/addresses/${id}/delete`, {}),  // Changed from DELETE to POST for IIS compatibility
};

/**
 * References and documentation API endpoints.
 */
export const referencesAPI = {
  downloadDocument: (filename) => {
    return api.get(`/references/documents/${filename}`, {
      responseType: 'blob',
    });
  },
  getHTSCodes: () => api.get('/references/hts-codes'),
};

/**
 * Notifications API endpoints.
 */
export const notificationsAPI = {
  getAll: () => api.get('/notifications'),
  getUnread: () => api.get('/notifications/unread'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.post('/notifications/mark-all-read'),  // Changed from PUT to POST for IIS compatibility
};

/**
 * Reports API endpoints.
 */
export const reportsAPI = {
  getSummary: (params) => api.get('/reports/summary', { params }),
  getShipments: (params) => api.get('/reports/shipments', { params }),
  exportExcel: (params) => {
    return api.get('/reports/export/excel', {
      params,
      responseType: 'blob',
    });
  },
  exportCSV: (params) => {
    return api.get('/reports/export/csv', {
      params,
      responseType: 'blob',
    });
  },
  exportPDF: (params) => {
    return api.get('/reports/export/pdf', {
      params,
      responseType: 'blob',
    });
  },
};

/**
 * Monitoring API endpoints.
 */
export const monitoringAPI = {
  getEmailLogs: (params) => api.get('/monitoring/email-logs', { params }),
  resendNotification: (shipmentId) => api.post(`/monitoring/shipments/${shipmentId}/resend-notification`),
  getSystemLogs: (params) => api.get('/monitoring/system-logs', { params }),
  getSystemHealth: () => api.get('/monitoring/system-health'),
};

export default api;
